import { HomeInsurancePremiumResponse } from '@app/domain/model/eshop/home/HomeInsurancePremiumResponse';
import { ProductDataRider } from '@app/domain/model/eshop/transport/ProductDataRider';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';

export function usePriceCalculation(
    currentPremiums: Nullable<HomeInsurancePremiumResponse>,
    allRiders: ProductDataRider[],
) {
    const [selectedRiskCode, setSelectedRiskCode] = useState<string>('');
    const [selectedRiskPackageCodes, setSelectedRiskPackageCodes] = useState<string[]>([]);

    const selectedRisk = useMemo(() => {
        if (!currentPremiums || !selectedRiskCode) return undefined;

        return currentPremiums.riskPackages.find(risk => risk.code === selectedRiskCode);
    }, [currentPremiums, selectedRiskCode]);

    const selectedRiders = useMemo(() => {
        return allRiders.filter(rider => selectedRiskPackageCodes.includes(rider.riskPackageCode));
    }, [allRiders, selectedRiskPackageCodes]);

    const totalPrice = useMemo(() => {
        if (!selectedRisk?.premiumAfterDiscount || !selectedRisk?.premium) return 0;

        const riskPrice =
            selectedRisk.premiumAfterDiscount && selectedRisk.premiumAfterDiscount > 0
                ? selectedRisk.premiumAfterDiscount
                : selectedRisk.premium || 0;

        const ridersPrice = selectedRiders.reduce((total, rider) => total + rider.premiums.premium, 0);

        return riskPrice + ridersPrice;
    }, [selectedRisk, selectedRiders]);

    const priceToPayNow = useMemo(() => {
        if (!currentPremiums?.paymentSchedule || currentPremiums.paymentSchedule.length <= 1) {
            return totalPrice;
        }

        const ridersPrice = selectedRiders.reduce((total, rider) => total + rider.premiums.premium, 0);

        return currentPremiums.paymentSchedule[0].amountAfterDiscount + ridersPrice;
    }, [currentPremiums?.paymentSchedule, totalPrice, selectedRiders]);

    const handleRisksChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        setSelectedRiskCode(prev => {
            if (prev !== value) {
                setSelectedRiskPackageCodes([]);
            }
            return value;
        });
    }, []);

    const handleRidersChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const { value, checked } = e.target;

        setSelectedRiskPackageCodes(prev => {
            if (checked) {
                return [...prev, value];
            } else {
                return prev.filter(code => code !== value);
            }
        });
    }, []);

    const updateSelectedRisks = useCallback(() => {
        setSelectedRiskCode(prev => {
            const exists = currentPremiums?.riskPackages.some(risk => risk.code === prev);
            return exists ? prev : '';
        });

        setSelectedRiskPackageCodes(prev => {
            return prev.filter(code => allRiders.some(rider => rider.riskPackageCode === code));
        });
    }, [currentPremiums, allRiders]);

    const initializeFromSession = useCallback((riskCode: string, riskPackageCodes: string[]) => {
        setSelectedRiskCode(riskCode);
        setSelectedRiskPackageCodes(riskPackageCodes);
    }, []);

    return {
        priceState: {
            selectedRisk,
            selectedRiskCode,
            totalPrice,
            priceToPayNow,
            selectedRiders,
        },
        handleRisksChange,
        handleRidersChange,
        updateSelectedRisks,
        initializeFromSession,
    };
}
